export const experiences = [
  {
    id: 1,
    company: "Digital Avenues",
    role: "Software Engineer (AI)",
    type: "Full-time · Remote",
    location: "Kolkata, IN",
    period: "May 2024 – Present",
    current: true,
    color: "#7c3aed",
    highlights: [
      "Architected a multi-agent A2A system (LangChain + LangGraph) and NL-to-SQL RAG pipeline (Pinecone + PGVector + OpenAI) for a US ad-tech client — ReAct agents with MCP-connected PostgreSQL, prompt caching, and cosine-similarity search cut campaign setup by 60% and turned hours of manual reporting into seconds for 50+ daily users",
      "Eliminated 70% of manual testing across 10+ projects by generating Playwright test cases from feature specs with OpenAI + Pytest suites; wired LangSmith for LLM tracing and evaluation to catch prompt regressions before production",
      "Dropped time-to-hire by 35% building an end-to-end LLM hiring agent (CV parsing → skill extraction → JIRA API reviewer assignment) shipped as a micro-frontend; deployed all services as Docker containers on GCP with CI/CD pipelines",
    ],
    tech: ["Python", "LangChain", "LangGraph", "FastAPI", "OpenAI", "Pinecone", "PGVector", "GCP", "Docker", "LangSmith", "MCP", "Playwright", "Pytest"],
  },
  {
    id: 2,
    company: "Emoneeds",
    role: "Software Developer",
    type: "Full-time",
    location: "Gurgaon, IN",
    period: "Nov 2022 – Apr 2024",
    current: false,
    color: "#06b6d4",
    highlights: [
      "Held up 500+ concurrent therapy sessions and cut API response time by 40% with Node.js backend, JWT auth, and asynchronous request handling",
      "Kept hot patient-data queries fast under load by layering Redis response caching over PostgreSQL and MongoDB",
      "Grew a psychologist platform from 0 to 1,000+ active users in 6 months (4.2/5 satisfaction rating)",
      "Integrated Terra health data, Zoom, and Razorpay SDKs with webhook handlers; scheduled nightly cron-based database backups",
      "Developed React frontend with role-based access control and protected routes",
    ],
    tech: ["Node.js", "React", "PostgreSQL", "MongoDB", "Redis", "JWT", "Docker", "Zoom SDK", "Razorpay"],
  },
  {
    id: 3,
    company: "Innomatics Research Labs",
    role: "Full Stack Developer Mentor",
    type: "Internship",
    location: "Hyderabad, IN",
    period: "Apr 2022 – Jul 2022",
    current: false,
    color: "#10b981",
    highlights: [
      "Coached 30+ students in Node.js, React, and data structures — 85% shipped a working project",
      "Rebuilt company landing pages in React, cutting load time by 25%",
    ],
    tech: ["Node.js", "React", "JavaScript", "HTML/CSS"],
  },
  {
    id: 4,
    company: "SymbioticNow",
    role: "Backend Developer",
    type: "Internship · Remote",
    location: "Remote, IN",
    period: "Dec 2021 – Jan 2022",
    current: false,
    color: "#f59e0b",
    highlights: [
      "Built the Spring Boot order-tracking backend with MongoDB; order processing time fell 30%",
      "Developed an internal React ops dashboard — team retired their spreadsheets entirely",
    ],
    tech: ["Spring Boot", "MongoDB", "React", "Java"],
  },
];

export const skillCategories = [
  {
    id: 1,
    title: "Agentic AI",
    icon: "🤖",
    color: "#7c3aed",
    skills: [
      "Multi-agent (A2A)",
      "LangChain",
      "LangGraph",
      "ReAct Agents",
      "MCP Integration",
      "Agent Memory",
      "Tool-using Agents",
      "AI Copilots",
      "Workflow Automation",
    ],
  },
  {
    id: 2,
    title: "Generative AI",
    icon: "🧠",
    color: "#06b6d4",
    skills: [
      "RAG Pipelines",
      "Pinecone",
      "Vector Search",
      "Embeddings",
      "Cosine Similarity",
      "Prompt Engineering",
      "Prompt Caching",
      "OpenAI API",
      "LangSmith",
    ],
  },
  {
    id: 3,
    title: "Backend",
    icon: "⚙️",
    color: "#8b5cf6",
    skills: [
      "Python",
      "FastAPI",
      "Flask",
      "Node.js",
      "RESTful APIs",
      "Async Programming",
      "GraphQL",
      "Background Workers",
      "Cron Jobs",
    ],
  },
  {
    id: 4,
    title: "Databases",
    icon: "🗄️",
    color: "#ec4899",
    skills: [
      "PostgreSQL",
      "PGVector",
      "SQL Server",
      "MongoDB",
      "Redis",
    ],
  },
  {
    id: 5,
    title: "Cloud & DevOps",
    icon: "☁️",
    color: "#10b981",
    skills: [
      "GCP",
      "Docker",
      "CI/CD",
      "Pytest",
      "Playwright",
      "Git",
      "JIRA",
    ],
  },
  {
    id: 6,
    title: "Frontend",
    icon: "🎨",
    color: "#f59e0b",
    skills: [
      "React",
      "TypeScript",
      "JavaScript",
      "Micro-frontend",
      "TailwindCSS",
    ],
  },
];

export const projects = [
  {
    id: 1,
    title: "Multi-Agent Campaign Automation",
    description:
      "A2A multi-agent system that cut campaign setup by 60% for a US ad-tech client. Specialist LangGraph agents plan, delegate, and execute real-time ad campaign workflows end-to-end.",
    tech: ["Python", "LangChain", "LangGraph", "FastAPI", "GCP", "Docker"],
    category: "Agentic AI",
    categoryColor: "#7c3aed",
    link: null,
    isPrivate: true,
    metric: "60% less manual effort",
  },
  {
    id: 2,
    title: "NL-to-SQL RAG Pipeline",
    description:
      "Turned hours of report-building into seconds for 50+ daily users. RAG pipeline with chunking, Pinecone vector search, OpenAI LLM, prompt caching — exposed via FastAPI + React frontend.",
    tech: ["Python", "OpenAI", "Pinecone", "FastAPI", "React", "LangSmith"],
    category: "Generative AI",
    categoryColor: "#06b6d4",
    link: null,
    isPrivate: true,
    metric: "50+ daily active users",
  },
  {
    id: 3,
    title: "PGVector Semantic Search",
    description:
      "Natural-language data lookup on an ad platform. Embedded name/description columns for cosine-similarity search. Background worker vectorizes every new record on insert; cron-scheduled index refreshes.",
    tech: ["Python", "PGVector", "PostgreSQL", "FastAPI"],
    category: "Generative AI",
    categoryColor: "#06b6d4",
    link: null,
    isPrivate: true,
    metric: "Real-time semantic search",
  },
  {
    id: 4,
    title: "LLM-Driven Hiring Agent",
    description:
      "Dropped time-to-hire by 35%. End-to-end hiring workflow: parses CVs, extracts skills, auto-assigns reviewers via JIRA API. Shipped as an independently deployable micro-frontend.",
    tech: ["Python", "OpenAI", "JIRA API", "React", "FastAPI"],
    category: "Agentic AI",
    categoryColor: "#7c3aed",
    link: null,
    isPrivate: true,
    metric: "35% faster time-to-hire",
  },
  {
    id: 5,
    title: "AI QA Automation Framework",
    description:
      "Eliminated 70% of manual testing across 10+ client projects. OpenAI generates Playwright test cases from feature specs; Pytest covers unit and integration layers.",
    tech: ["Python", "OpenAI", "Playwright", "Pytest"],
    category: "DevOps",
    categoryColor: "#10b981",
    link: null,
    isPrivate: true,
    metric: "70% less manual testing",
  },
  {
    id: 6,
    title: "Recipe Platform",
    description:
      "Full-stack recipe platform with React CMS and public UI. GraphQL filtering, JWT auth, WebSocket real-time alerts, and a complete recipe management CMS.",
    tech: ["GraphQL", "Express.js", "React", "MongoDB", "JWT", "WebSockets"],
    category: "Full-Stack",
    categoryColor: "#f59e0b",
    link: "https://github.com/hardikralhan",
    isPrivate: false,
    metric: "Open source",
  },
];

export const stats = [
  { value: 4, suffix: "+", label: "Years Experience", color: "#a78bfa" },
  { value: 10, suffix: "+", label: "Client Projects", color: "#67e8f9" },
  { value: 50, suffix: "+", label: "Daily Active Users", color: "#6ee7b7" },
  { value: 70, suffix: "%", label: "Workflow Reduction", color: "#fcd34d" },
];
